import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteTransferOrderVariantMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  logError,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function WarehouseTransferOrderVariants() {
  const navigate = useNavigate();

  const [deleteTransferOrderVariant] = useDeleteTransferOrderVariantMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    match(actionKey)
      .with('redirectDetail', 'edit', () => {
        const id = isArray(rowId) ? head(rowId) : rowId;
        navigate(composePath(settingsRoutes.warehousesTransferOrderVariantsDetail, {params: {id}}));
      })
      .with('delete', () => {
        const id = isArray(rowId) ? head(rowId) : rowId;

        if (isNil(id)) {
          return logError('transferOrderVariantId is not defined');
        }

        openDeleteDialog({
          onConfirm: () =>
            deleteTransferOrderVariant({transferOrderVariantId: id})
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(() => refreshData())
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.warehouse.labels.transferOrderVariants'),
    actions: [
      {
        type: 'button',
        title: i18n.t('entity.warehouse.actions.newTransferOrderVariant'),
        onClick: () => navigate(settingsRoutes.warehousesTransferOrderVariantsCreate),
      },
    ],
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.transferOrderVariants')} />
      <SettingsTemplate
        data-testid={testIds.settings.warehousesTransferOrderVariants('template')}
        header={header}
      >
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.transferOrderVariants}
          actionCallback={actionCallback}
        />
      </SettingsTemplate>
    </>
  );
}
